import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Wallet, Printer, XCircle, Download, Sparkles, Settings } from 'lucide-react'
import api from '../api'
import { Card, PageHeader, Badge, EmptyState, Table, THead, td, Field, Button, Alert, Banner, Modal, ModalHeader, Input, Select } from '../components/ui'
import SlipDoc from '../components/SlipDoc'

function rp(n) { return 'Rp ' + Number(n || 0).toLocaleString('id-ID') }
function bulanIni() { return new Date().toISOString().slice(0, 7) }

// Bungkus field dgn tanda kutip kalau mengandung koma/kutip/baris baru -- pola sama persis dgn
// PelangganKontak.jsx (export CSV client-side, tidak perlu endpoint server-side baru).
function csvField(value) {
  const s = String(value ?? '')
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}
function downloadCsv(filename, rows) {
  const csv = rows.map(row => row.map(csvField).join(',')).join('\r\n')
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

export default function Payroll() {
  const [periode, setPeriode] = useState(bulanIni())
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)

  const [buatModal, setBuatModal] = useState(null) // item pegawai yg lagi dibuatkan slip
  const [cicilanKasbon, setCicilanKasbon] = useState('')
  const [bonus, setBonus] = useState('')
  const [error, setError] = useState('')
  const [saving, setSaving] = useState(false)
  const [aiLoading, setAiLoading] = useState(false)
  const [aiCatatan, setAiCatatan] = useState('')
  const [aiError, setAiError] = useState('')

  const [lihatSlip, setLihatSlip] = useState(null)

  // Pengaturan Evaluasi AI (2026-08-04) -- platform+model OpenRouter khusus fitur ini, terpisah
  // dari chatbot WA (lihat komentar aiEvaluator.js). Form dibuka otomatis kalau belum diatur sama
  // sekali, supaya admin tidak bingung kenapa tombol "Evaluasi AI" gagal tanpa penjelasan jelas.
  const [platforms, setPlatforms] = useState([])
  const [aiSettings, setAiSettings] = useState({ platform_id: '', model: '' })
  const [aiSettingsForm, setAiSettingsForm] = useState({ platform_id: '', model: '' })
  const [aiSettingsOpen, setAiSettingsOpen] = useState(false)
  const [savingAiSettings, setSavingAiSettings] = useState(false)
  const [aiSettingsInfo, setAiSettingsInfo] = useState('')

  useEffect(() => { load() }, [periode])
  useEffect(() => { loadAiSettings() }, [])

  async function load() {
    setLoading(true)
    try {
      const res = await api.get('/payroll', { params: { periode } })
      setData(res.data)
    } finally { setLoading(false) }
  }

  async function loadAiSettings() {
    const [platformRes, settingRes] = await Promise.all([
      api.get('/ai-platforms'),
      api.get('/payroll/pengaturan-ai')
    ])
    setPlatforms(platformRes.data)
    setAiSettings(settingRes.data)
    setAiSettingsForm({ platform_id: settingRes.data.platform_id, model: settingRes.data.model })
    if (!settingRes.data.platform_id || !settingRes.data.model) setAiSettingsOpen(true)
  }

  async function simpanAiSettings() {
    setSavingAiSettings(true)
    setAiSettingsInfo('')
    try {
      await api.post('/payroll/pengaturan-ai', aiSettingsForm)
      setAiSettings(aiSettingsForm)
      setAiSettingsOpen(false)
    } catch (e) {
      setAiSettingsInfo(e.response?.data?.message || 'Gagal menyimpan pengaturan')
    } finally { setSavingAiSettings(false) }
  }

  function bukaBuatSlip(item) {
    setBuatModal(item)
    // Bonus Target (2026-08-04) -- prefill dari saran server (realisasi/target x budget, dibulatkan
    // ke atas kelipatan 10.000, dibatasi maks budget -- lihat hitungBonusTarget() di
    // routes/payroll.js), 0 kalau target/budget belum diatur di halaman Target Kerja. TETAP bisa
    // diedit manual admin sblm klik Simpan Slip. ("Potongan Lain" / kekurangan capaian Bonus Target
    // DIHAPUS TOTAL 2026-08-10 -- tidak lagi ada di form ini, lihat komentar server/routes/payroll.js)
    setBonus(item.preview.bonus_target_disarankan ? String(item.preview.bonus_target_disarankan) : '')
    setCicilanKasbon(''); setError('')
    setAiCatatan(''); setAiError('')
  }

  // AI hanya menulis CATATAN NARATIF (kedisiplinan, tren kerja, dll) -- angka bonus/potongan sudah
  // dihitung matematis (lihat bukaBuatSlip), bukan lagi dari AI sejak 2026-08-04 (model murah
  // konsisten menjawab angka generik saat diminta menebak potongan "pakai penilaian", tidak
  // mencerminkan data nyata). Admin tetap yg review & klik Simpan Slip.
  async function evaluasiAI() {
    setAiLoading(true); setAiError(''); setAiCatatan('')
    try {
      const res = await api.post('/payroll/evaluasi-ai', { teknisi_id: buatModal.teknisi_id, periode })
      setAiCatatan(res.data.catatan_evaluasi)
    } catch (e) {
      setAiError(e.response?.data?.message || 'Gagal memuat evaluasi AI')
    } finally { setAiLoading(false) }
  }

  const cicilanNum = Number(cicilanKasbon) || 0
  const sisaKasbonSetelahIni = buatModal ? buatModal.preview.kasbon_outstanding - cicilanNum : 0
  const previewTotal = buatModal
    ? (buatModal.preview.gaji_pokok + buatModal.preview.uang_makan_transport + (Number(bonus) || 0) + buatModal.preview.bonus_jenis_pekerjaan)
      - buatModal.preview.potongan_bpjs - cicilanNum
    : 0

  async function simpanSlip() {
    setSaving(true)
    setError('')
    try {
      await api.post('/payroll', {
        teknisi_id: buatModal.teknisi_id, periode,
        bonus: Number(bonus) || 0, cicilan_kasbon: cicilanNum
      })
      setBuatModal(null)
      load()
    } catch (e) {
      setError(e.response?.data?.message || 'Gagal membuat slip')
    } finally { setSaving(false) }
  }

  async function bukaLihatSlip(slipId) {
    const res = await api.get(`/payroll/${slipId}`)
    setLihatSlip(res.data)
  }

  async function batalkanSlip(slipId) {
    if (!window.confirm('Batalkan slip ini? Cicilan kasbon yang tercatat lewat slip ini akan kembali ke saldo.')) return
    await api.delete(`/payroll/${slipId}`)
    setLihatSlip(null)
    load()
  }

  function exportCsv() {
    const rows = [['Nama', 'Jabatan', 'Gaji Pokok', 'Makan & Transport', 'Bonus', 'Bonus Jenis Pekerjaan', 'Potongan BPJS', 'Saldo Kasbon Sebelum', 'Cicilan Kasbon', 'Sisa Kasbon Sesudah', 'Potongan Lain', 'Tabungan Sebelum', 'Tabungan Sesudah', 'Total Diterima']]
    data.filter(d => d.slip).forEach(d => rows.push([
      d.nama, d.jabatan || '', d.slip.gaji_pokok, d.slip.uang_makan_transport, d.slip.bonus, d.slip.bonus_jenis_pekerjaan,
      d.slip.potongan_bpjs, d.slip.kasbon_saldo_sebelum, d.slip.potongan_kasbon, d.slip.kasbon_saldo_sesudah, d.slip.potongan_lain,
      d.slip.tabungan_saldo_sebelum, d.slip.tabungan_saldo_sesudah, d.slip.total_diterima
    ]))
    downloadCsv(`payroll-${periode}.csv`, rows)
  }

  return (
    <div>
      <PageHeader title="Payroll" subtitle="Slip gaji bulanan pegawai — take-home pay bersih, tanpa perhitungan PPh21" />

      {/* Pengaturan Evaluasi AI (2026-08-04) -- platform+model OpenRouter khusus fitur "Evaluasi AI"
          di modal Buat Slip, terpisah dari chatbot WA (lihat komentar server/aiEvaluator.js).
          Terbuka otomatis kalau belum pernah diatur. */}
      <Card style={{ padding: 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Sparkles size={16} />
            <span style={{ fontWeight: 600, fontSize: 13.5 }}>Evaluasi AI</span>
            <Badge tone={aiSettings.platform_id && aiSettings.model ? 'success' : 'warning'}>
              {aiSettings.platform_id && aiSettings.model ? 'Sudah diatur' : 'Belum diatur'}
            </Badge>
          </div>
          <Button size="sm" variant="soft" icon={Settings} onClick={() => setAiSettingsOpen(o => !o)}>
            {aiSettingsOpen ? 'Tutup' : 'Atur Platform & Model'}
          </Button>
        </div>

        {aiSettingsOpen && (
          <div style={{ marginTop: 16, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
            {platforms.length === 0 ? (
              <Alert type="error">
                Belum ada platform AI. Tambahkan dulu di halaman <Link to="/ai-platforms">Platform AI</Link> (mis. OpenRouter) sebelum bisa mengatur fitur ini.
              </Alert>
            ) : (
              <>
                {aiSettingsInfo && <Alert type="error">{aiSettingsInfo}</Alert>}
                <p style={{ fontSize: 12.5, color: 'var(--text-muted)', marginTop: 0, marginBottom: 14 }}>
                  Dipakai tombol "Evaluasi AI" di modal Buat Slip -- AI membaca rekap pekerjaan teknisi & menulis catatan evaluasi kualitatif (bonus/potongan dihitung otomatis oleh sistem, bukan AI).
                </p>
                <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                  <Field label="Platform" style={{ flex: '0 0 200px' }}>
                    <Select value={aiSettingsForm.platform_id} onChange={e => setAiSettingsForm({ ...aiSettingsForm, platform_id: e.target.value })}>
                      <option value="">-- pilih platform --</option>
                      {platforms.map(p => <option key={p.id} value={p.id} disabled={!p.aktif}>{p.nama}{!p.aktif ? ' (nonaktif)' : ''}</option>)}
                    </Select>
                  </Field>
                  <Field label="Model" style={{ flex: 1, minWidth: 220 }}>
                    <Input value={aiSettingsForm.model} onChange={e => setAiSettingsForm({ ...aiSettingsForm, model: e.target.value })} placeholder="mis. anthropic/claude-opus-4.8" style={{ fontFamily: 'monospace', fontSize: 12.5 }} />
                  </Field>
                </div>
                <Button loading={savingAiSettings} onClick={simpanAiSettings}>Simpan Platform & Model</Button>
                <p style={{ fontSize: 12.5, color: 'var(--text-faint)', marginTop: 12 }}>Slug model ikut format platform yg dipilih (utk OpenRouter, salin dari <span style={{ fontFamily: 'monospace' }}>openrouter.ai/models</span>). Pilih model yang cocok untuk penilaian (bukan sekadar cepat/murah) -- ini menyentuh keputusan potongan gaji.</p>
              </>
            )}
          </div>
        )}
      </Card>

      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 16 }}>
        <Field label="Periode">
          <Input type="month" value={periode} onChange={e => setPeriode(e.target.value)} style={{ width: 180 }} />
        </Field>
        <Button variant="soft" icon={Download} onClick={exportCsv} disabled={!data.some(d => d.slip)}>Export CSV</Button>
      </div>

      <Table>
        <THead cols={['Pegawai', 'Target/Realisasi', 'Tabungan', 'Komponen', 'Status', '']} />
        <tbody>
          {loading ? (
            <tr><td colSpan={6}><EmptyState icon={Wallet} title="Memuat..." /></td></tr>
          ) : data.length === 0 ? (
            <tr><td colSpan={6}><EmptyState icon={Wallet} title="Belum ada pegawai aktif" /></td></tr>
          ) : data.map(item => (
            <tr key={item.teknisi_id}>
              <td style={{ ...td, fontWeight: 600 }}>{item.nama}<div style={{ fontWeight: 400, fontSize: 11.5, color: 'var(--text-muted)' }}>{item.jabatan || '-'}</div></td>
              <td style={td}>
                {item.target_pekerjaan_bulanan
                  ? <Badge tone={item.realisasi_pekerjaan >= item.target_pekerjaan_bulanan ? 'success' : 'warning'}>{item.realisasi_pekerjaan} / {item.target_pekerjaan_bulanan}</Badge>
                  : <span style={{ color: 'var(--text-faint)' }}>{item.realisasi_pekerjaan} selesai (tanpa target)</span>}
              </td>
              <td style={td}>
                {item.tabungan_saldo > 0
                  ? <span style={{ color: 'var(--success)', fontWeight: 500 }}>{rp(item.tabungan_saldo)}</span>
                  : <span style={{ color: 'var(--text-faint)' }}>-</span>}
              </td>
              <td style={td}>
                {item.slip ? rp(item.slip.total_diterima) : (
                  <span style={{ color: 'var(--text-muted)' }}>
                    {rp(item.preview.gaji_pokok + item.preview.uang_makan_transport + item.preview.bonus_jenis_pekerjaan)}
                    {item.preview.kasbon_outstanding > 0 && <> — kasbon {rp(item.preview.kasbon_outstanding)}</>}
                  </span>
                )}
              </td>
              <td style={td}>
                {item.slip ? <Badge tone="success">Sudah dibuat</Badge> : <Badge tone="neutral">Belum dibuat</Badge>}
              </td>
              <td style={td}>
                {item.slip
                  ? <Button size="sm" variant="soft" onClick={() => bukaLihatSlip(item.slip.id)}>Lihat</Button>
                  : <Button size="sm" onClick={() => bukaBuatSlip(item)}>Buat Slip</Button>}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Modal buat slip */}
      {buatModal && (
        <Modal onClose={() => setBuatModal(null)} width={480}>
          <ModalHeader title={`Buat Slip — ${buatModal.nama}`} subtitle={`Periode ${periode}`} onClose={() => setBuatModal(null)} />
          {error && <Alert type="error">{error}</Alert>}

          <div style={{ fontSize: 13, marginBottom: 14, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Gaji Pokok</span><span>{rp(buatModal.preview.gaji_pokok)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Makan &amp; Transport</span><span>{rp(buatModal.preview.uang_makan_transport)}</span></div>
            {buatModal.preview.bonus_jenis_pekerjaan > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--success)' }}><span>Bonus Jenis Pekerjaan (otomatis)</span><span>{rp(buatModal.preview.bonus_jenis_pekerjaan)}</span></div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', color: 'var(--danger)' }}><span>Potongan BPJS</span><span>-{rp(buatModal.preview.potongan_bpjs)}</span></div>
          </div>

          <Button variant="soft" size="sm" icon={Sparkles} loading={aiLoading} onClick={evaluasiAI} style={{ marginBottom: 14, width: '100%', justifyContent: 'center' }}>
            Evaluasi AI
          </Button>
          {aiError && <Alert type="error">{aiError}</Alert>}
          {aiCatatan && <Banner tone="brand">{aiCatatan}</Banner>}

          <Field label="Bonus (Rp)" style={{ marginBottom: 4 }}>
            <Input type="number" value={bonus} onChange={e => setBonus(e.target.value)} placeholder="0" />
          </Field>
          {buatModal.target_pekerjaan_bulanan > 0 && (
            <p style={{ fontSize: 12, color: 'var(--text-faint)', marginTop: 0, marginBottom: 14 }}>
              Terisi otomatis dari Bonus Target ({buatModal.realisasi_pekerjaan}/{buatModal.target_pekerjaan_bulanan} pekerjaan) -- tetap bisa diubah manual.
            </p>
          )}

          {buatModal.preview.kasbon_outstanding > 0 && (
            <Field label={`Cicilan Kasbon (Rp) — Saldo ${rp(buatModal.preview.kasbon_outstanding)}`} style={{ marginBottom: 8 }}>
              <Input type="number" value={cicilanKasbon} onChange={e => setCicilanKasbon(e.target.value)} placeholder="0 -- boleh sebagian, tidak wajib lunas" />
            </Field>
          )}
          {cicilanNum > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>Sisa kasbon setelah ini: {rp(sisaKasbonSetelahIni)}</div>
          )}

          <div style={{ borderTop: '1px solid var(--border)', margin: '14px 0', paddingTop: 14, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: 15 }}>
            <span>Total Diterima</span><span>{rp(previewTotal)}</span>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="neutral" onClick={() => setBuatModal(null)} style={{ flex: 1, justifyContent: 'center' }}>Batal</Button>
            <Button loading={saving} onClick={simpanSlip} style={{ flex: 1, justifyContent: 'center' }}>Simpan Slip</Button>
          </div>
        </Modal>
      )}

      {/* Modal lihat/cetak slip -- pratinjau layar pakai komponen SlipDoc yg sama dgn halaman cetak,
          supaya WYSIWYG (apa yg dilihat admin di sini = persis apa yg tercetak). Tombol Cetak membuka
          /payroll/:id/cetak di tab baru (bukan window.print() di modal ini) -- lihat komentar di
          PayrollSlipCetak.jsx utk alasannya. */}
      {lihatSlip && (
        <Modal onClose={() => setLihatSlip(null)} width={640}>
          <ModalHeader title="Slip Gaji" onClose={() => setLihatSlip(null)} />
          <SlipDoc slip={lihatSlip} />

          <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
            <Button variant="danger-soft" icon={XCircle} onClick={() => batalkanSlip(lihatSlip.id)}>Batalkan</Button>
            <Button icon={Printer} onClick={() => window.open(`/payroll/${lihatSlip.id}/cetak`, '_blank')} style={{ flex: 1, justifyContent: 'center' }}>Cetak</Button>
          </div>
        </Modal>
      )}
    </div>
  )
}
